import React, { useEffect, useRef, useState } from "react";
import { Button } from "reactstrap";
import { toast } from "react-toastify";
import "./DamagesForm.css";

import DamageImage from "../transactions/DamageImage";
import { compressImageFile } from "../utility/compressImageFile";

const MAX_DAMAGE_PHOTO_COUNT = 6;
const MAX_RAW_UPLOAD_BYTES = 8 * 1024 * 1024;
const MAX_COMPRESSED_UPLOAD_BYTES = 2 * 1024 * 1024;

function createPhotoDraftId() {
    return `damage-photo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeIncomingPhotos(initialDamagePhotos) {
    if (!Array.isArray(initialDamagePhotos)) return [];

    return initialDamagePhotos
        .map((photo) => {
            if (photo instanceof File) {
                return photo;
            }

            if (photo?.file instanceof File) {
                return photo.file;
            }

            return null;
        })
        .filter(Boolean)
        .slice(0, MAX_DAMAGE_PHOTO_COUNT);
}

function isMobileCaptureDevice() {
    if (typeof navigator === "undefined" || typeof window === "undefined") return false;

    const ua = String(navigator.userAgent || "");
    const uaDataMobile = typeof navigator.userAgentData?.mobile === "boolean" ? navigator.userAgentData.mobile : false;
    const maxTouchPoints = Number(navigator.maxTouchPoints || 0);
    const coarsePointer = typeof window.matchMedia === "function" ? window.matchMedia("(pointer: coarse)").matches : false;
    const matchesMobileUa = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);

    return Boolean(uaDataMobile || matchesMobileUa || (maxTouchPoints > 1 && coarsePointer));
}

const DamagesForm = ({ onSubmit, initialDamagePositions, initialDamagePhotos }) => {
    const previewUrlsRef = useRef(new Set());
    const [damagePositions, setDamagePositions] = useState(initialDamagePositions || []);
    const [damagePhotos, setDamagePhotos] = useState(() =>
        normalizeIncomingPhotos(initialDamagePhotos).map((file) => {
            const previewUrl = URL.createObjectURL(file);
            previewUrlsRef.current.add(previewUrl);
            return {
                id: createPhotoDraftId(),
                file,
                previewUrl,
            };
        }),
    );
    const canCapturePhotoOnDevice = isMobileCaptureDevice();

    useEffect(
        () => () => {
            previewUrlsRef.current.forEach((previewUrl) => {
                URL.revokeObjectURL(previewUrl);
            });
            previewUrlsRef.current.clear();
        },
        [],
    );

    const handleImageClick = (point) => {
        setDamagePositions((prev) => [...prev, point]);
    };

    const handleSubmit = () => {
        if (typeof onSubmit === "function") {
            onSubmit({
                positions: damagePositions,
                photos: damagePhotos.map((photo) => photo.file),
            });
        }
    };

    const handleUndoLastDot = () => {
        setDamagePositions((prev) => prev.slice(0, -1));
    };

    const handleRemovePhoto = (photoId) => {
        setDamagePhotos((prev) => {
            const toRemove = prev.find((photo) => photo.id === photoId);
            if (toRemove?.previewUrl) {
                URL.revokeObjectURL(toRemove.previewUrl);
                previewUrlsRef.current.delete(toRemove.previewUrl);
            }
            return prev.filter((photo) => photo.id !== photoId);
        });
    };

    const handlePhotoSelection = async (event) => {
        const selectedFiles = Array.from(event.target.files || []);
        event.target.value = "";

        if (selectedFiles.length === 0) return;

        const remainingSlots = MAX_DAMAGE_PHOTO_COUNT - damagePhotos.length;
        if (remainingSlots <= 0) {
            toast.error(`Photo limit reached (${MAX_DAMAGE_PHOTO_COUNT} per visit).`, { autoClose: 1000 });
            return;
        }

        if (selectedFiles.length > remainingSlots) {
            toast.error(`Only ${remainingSlots} more photo${remainingSlots === 1 ? "" : "s"} can be added.`, { autoClose: 1200 });
        }

        const filesToProcess = selectedFiles.slice(0, remainingSlots);
        const nextPhotos = [];

        for (const file of filesToProcess) {
            const mimeType = String(file?.type || "").toLowerCase();

            if (mimeType && !mimeType.startsWith("image/")) {
                toast.error("Only image files are allowed.", { autoClose: 1200 });
                continue;
            }

            if (file.size > MAX_RAW_UPLOAD_BYTES) {
                toast.error("Photo is too large before compression (8MB max).", { autoClose: 1200 });
                continue;
            }

            try {
                const { file: compressedFile } = await compressImageFile(file, {
                    maxDimension: 1280,
                    quality: 0.72,
                    outputMimeType: "image/jpeg",
                    outputExtension: "jpg",
                });

                if (compressedFile.size > MAX_COMPRESSED_UPLOAD_BYTES) {
                    toast.error("Compressed photo is still too large (2MB max).", { autoClose: 1200 });
                    continue;
                }

                const previewUrl = URL.createObjectURL(compressedFile);
                previewUrlsRef.current.add(previewUrl);

                nextPhotos.push({
                    id: createPhotoDraftId(),
                    file: compressedFile,
                    previewUrl,
                });
            } catch (_err) {
                toast.error("Unable to process selected photo.", { autoClose: 1200 });
            }
        }

        if (nextPhotos.length > 0) {
            setDamagePhotos((prev) => [...prev, ...nextPhotos]);
        }
    };

    return (
        <div className="damagesFormWrapper">
            <form
                className="damagesForm"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                }}>
                <div className="damagesFormTitle">Damage Assessment</div>
                <div className="formGroup">
                    <DamageImage
                        damagePositions={damagePositions}
                        onImageClick={handleImageClick}
                    />
                </div>
                <div className="damagesActions">
                    <Button
                        type="button"
                        className="damagesBtn damagesBtnSecondary"
                        disabled={damagePositions.length === 0}
                        onClick={handleUndoLastDot}>
                        Undo Last Dot
                    </Button>
                </div>

                <div className="damagePhotoSection">
                    {canCapturePhotoOnDevice ? (
                        <>
                            <div className="damagePhotoPickerActions">
                                <label
                                    htmlFor="damage-photo-camera-input"
                                    className="damagesBtn damagesBtnSecondary damagePhotoPickerBtn">
                                    Take Damage Photo
                                </label>
                            </div>
                            <input
                                id="damage-photo-camera-input"
                                type="file"
                                accept="image/*"
                                capture="environment"
                                className="damagePhotoInput"
                                onChange={handlePhotoSelection}
                            />
                            <div className="damagePhotoHint">Tap "Take Damage Photo" again for each additional photo.</div>
                        </>
                    ) : (
                        <div className="damagePhotoHint">Damage photo capture is available on mobile devices only.</div>
                    )}

                    <div className="damagePhotoMeta">
                        {damagePhotos.length} / {MAX_DAMAGE_PHOTO_COUNT} photos selected
                    </div>

                    {damagePhotos.length > 0 ? (
                        <div className="damagePhotoGrid">
                            {damagePhotos.map((photo, index) => (
                                <div
                                    key={photo.id}
                                    className="damagePhotoCard">
                                    <img
                                        src={photo.previewUrl}
                                        alt={`Damage ${index + 1}`}
                                        className="damagePhotoPreview"
                                    />
                                    <button
                                        type="button"
                                        className="damagePhotoRemoveBtn"
                                        onClick={() => handleRemovePhoto(photo.id)}>
                                        Remove
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>

                <button
                    type="submit"
                    className="damagesBtn damagesSubmitBtn">
                    Save Damages & Photos
                </button>
            </form>
        </div>
    );
};

export default DamagesForm;
